import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTriggerButton,
} from '@customerio/pluma-components/react';
import { useRouter } from '@tanstack/react-router';
import { usePermissions } from '@customerio/hydra-permissions/react';
import type { Permission } from '@customerio/hydra-permissions';
import { useAuth } from '@/services/auth';
import { AccountFeatureFlag, useFeatureFlag } from '@/services/auth/feature-flags';
import { isBuilderPlan } from '@/services/auth/plan-slugs';
import { generatePath } from '@/services/router/generate-path';
import { listUseInActions, type CreatableTarget, type UseInAction } from '../utils/use-in-actions';
import { withContentUsage } from '../utils/usage-surfaces';
import type { DataIndexItemRef } from '../utils/item';
import { useCreateFromItem } from './use-create-from-item';

/**
 * "Use in": building something out of the item being read.
 *
 * Five things an attribute or event can seed. Three of them are writes
 * and two are links; which are offered depends on the plan, the
 * workspace and the viewer's permissions, and an object or relationship
 * attribute is offered all five disabled with the reason, because it
 * describes an object rather than a person.
 */

type UseInMenuProps = {
  workspaceId: number;
  item: DataIndexItemRef;
  /** `sm` beside a drawer's usage line, `md` on the item page's header. */
  size?: 'sm' | 'md';
};

export function UseInMenu({ workspaceId, item, size = 'sm' }: UseInMenuProps) {
  const router = useRouter();
  const auth = useAuth();
  const { hasPermission } = usePermissions();
  const withContentFlag = useFeatureFlag(AccountFeatureFlag.ShowTemplatesSnippetsLayouts);
  const create = useCreateFromItem(workspaceId, item);
  const isBuilder = isBuilderPlan(auth.account);

  const actions = listUseInActions({
    item,
    isBuilder,
    // The flag gates the two object kinds only: a profile attribute or an
    // event offers a snippet and a template whatever the workspace shows
    // for object content usage.
    withTemplatesSnippetsLayouts: withContentUsage({
      kind: item.kind,
      isBuilder,
      withTemplatesSnippetsLayouts: withContentFlag,
    }),
    can: (target) => hasPermission(CREATE_PERMISSION[target]),
    hrefs: {
      snippet: generatePath(
        '/workspaces/:workspaceId/journeys/snippets/new',
        { workspaceId: String(workspaceId) },
        { attribute: item.id },
      ),
      template: router.buildLocation({
        to: '/workspaces/$workspaceId/journeys/layouts/starters',
        params: { workspaceId: String(workspaceId) },
      }).href,
    },
  });

  return (
    // Escape closes the menu and nothing else. Pluma's menus pass it on to
    // whatever they sit in, so inside the drawer one press closed both — and
    // the page this replaces closes only the menu.
    <DropdownMenu floatingPluginOptions={{ dismiss: { bubbles: { escapeKey: false } } }}>
      <DropdownMenuTriggerButton size={size} variant="secondary" icon="chevron-down" iconPosition="trailing">
        Use in
      </DropdownMenuTriggerButton>
      <DropdownMenuContent>
        {actions.map((action) => (
          <UseInItem key={action.target} action={action} create={create} />
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/** Which permission each of the three writes asks for. */
const CREATE_PERMISSION = {
  segment: 'journeys/segments/create',
  campaign: 'journeys/campaigns/create',
  newsletter: 'journeys/newsletters/create',
} as const satisfies Record<CreatableTarget, Permission>;

type UseInItemProps = {
  action: UseInAction;
  create: ReturnType<typeof useCreateFromItem>;
};

/** One row: a link, or a write that reports itself while it runs. */
function UseInItem({ action, create }: UseInItemProps) {
  if (action.href != null) {
    return (
      <DropdownMenuItem
        icon={action.icon}
        href={action.href}
        isExternal={action.isExternal}
        isDisabled={action.isDisabled}
        tooltip={action.tooltip}
      >
        {action.label}
      </DropdownMenuItem>
    );
  }

  const { target } = action;

  return (
    <DropdownMenuItem
      icon={action.icon}
      // Refused while another is in flight, so two clicks cannot make two
      // records from one item.
      isDisabled={action.isDisabled || create.pending != null}
      tooltip={action.tooltip}
      onClick={() => void create.run(target)}
    >
      {action.label}
    </DropdownMenuItem>
  );
}
